/**
 * CrossOriginFrameGuard — when OfficeHub is framed by a DIFFERENT origin, render
 * ONLY the embed-only tree on a single embed form path (/forms/embed/<slug>), and
 * nothing on any other path. The full app tree never mounts in a foreign frame.
 *
 * LIMIT: "framed by another origin" is judged against window.top, not the direct
 * parent. A same-origin page nested inside a foreign frame reads as cross-origin
 * (fail-closed); full-page loads are still governed by the X-Frame-Options /
 * frame-ancestors headers in firebase.json.
 *
 * WHY: /forms/embed/* is served with headers that let the public website frame it
 * (firebase.json). The website is same-site with app.onpointgen.com, so a staff
 * session cookie rides into that frame. If anything inside the frame ever reaches
 * a non-embed route — a route-table mistake, a <Navigate>, a link — the logged-in
 * app would render inside a page the website controls. The route table is the
 * first defence (modules/forms/routes.tsx 'embed/*'); this is the backstop.
 *
 * It sits directly inside <Router> and OUTSIDE AuthProvider (App.tsx), so a
 * blocked frame never mounts auth, org or data providers.
 *
 * Framed by OfficeHub itself (same origin) and top-level visits are unaffected.
 */
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/** Exactly one slug segment, optional trailing slash. Case-sensitive, like the route. */
const EMBED_FORM_PATH = /^\/forms\/embed\/[^/]+\/?$/;

export function isEmbedFormPath(pathname: string): boolean {
  return EMBED_FORM_PATH.test(pathname);
}

/**
 * True when this window is inside a frame whose top-level page is another origin.
 * Reading a cross-origin top's location throws; that is treated as cross-origin.
 */
export function isCrossOriginFramed(win: Window = window): boolean {
  try {
    if (win.self === win.top) return false;
  } catch {
    return true;
  }
  try {
    return win.top?.location.origin !== win.location.origin;
  } catch {
    return true;
  }
}

interface CrossOriginFrameGuardProps {
  /** The full app tree: rendered at top level and when framed by OfficeHub itself. */
  children: ReactNode;
  /**
   * What renders when framed by ANOTHER origin on /forms/embed/<slug> — the
   * embed-only tree (lib/embed/EmbedOnlyApp.tsx), never `children`. The full app
   * tree (auth, org, sync, navigation with sign-out) must not mount inside a page
   * another site controls, even on an allowed embed path (PR #5628 round 2).
   */
  framedEmbed: ReactNode;
  /** Injected in tests; defaults to the real window. */
  win?: Window;
}

export function CrossOriginFrameGuard({ children, framedEmbed, win }: CrossOriginFrameGuardProps) {
  const { pathname } = useLocation();
  if (!isCrossOriginFramed(win)) return <>{children}</>;
  return isEmbedFormPath(pathname) ? <>{framedEmbed}</> : null;
}
